import { useState, useEffect, useCallback } from 'react';
import announcementsData from '../data/announcements.json';

const STORAGE_KEY = 'puzzlists_announcements';
const MAX_ANNOUNCEMENT_AGE_DAYS = 30;

// Get stored announcement data from localStorage
const getStoredData = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

// Save announcement data to localStorage
const saveStoredData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

// Check if an announcement is within age limit (in days)
const isWithinAgeDays = (dateStr, days) => {
  const announcementDate = new Date(dateStr);
  const now = new Date();
  const diffMs = now - announcementDate;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= days;
};

export function useAnnouncement() {
  const [announcement, setAnnouncement] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Get the latest announcement
    const { announcements } = announcementsData;
    if (!announcements || announcements.length === 0) {
      return undefined;
    }

    const latest = announcements[0];
    const stored = getStoredData();

    // Check if already dismissed
    if (stored?.dismissedId === latest.id) {
      return undefined;
    }

    // Check if announcement is too old (> 30 days)
    if (!isWithinAgeDays(latest.date, MAX_ANNOUNCEMENT_AGE_DAYS)) {
      return undefined;
    }

    // Show the announcement with a small delay for better UX
    const timer = setTimeout(() => {
      setAnnouncement(latest);
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const dismissAnnouncement = useCallback(() => {
    if (!announcement) return;

    const stored = getStoredData();
    saveStoredData({
      dismissedId: announcement.id,
      lastVisit: stored?.lastVisit || Date.now(),
    });

    setIsVisible(false);
    // Delay clearing announcement to allow fade-out animation if needed
    setTimeout(() => setAnnouncement(null), 300);
  }, [announcement]);

  return {
    announcement: isVisible ? announcement : null,
    dismissAnnouncement,
  };
}

export default useAnnouncement;
